import { useEffect, useState } from 'react';
import { Box, Grid, IconButton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, AppState } from 'store';
import { IReadImageRequest } from '../../../types/IApiTypes';
import { getImages } from '../../store/slice/ConfigurationSlice';
import productImageNotFound from '../../assets/ProductImageNotFound.png';
import { getPackageDetails } from '../../services/ConfigurationDataHelperFunctions';

/**
 * Product images component
 * @returns  React element
 */
const ProductImageGallery = (): React.JSX.Element => {
  const dispatch = useDispatch<AppDispatch>();

  const [currentIndex, setCurrentIndex] = useState(0);

  const { images, productId, packagePath } = useSelector((state: AppState) => state.configuration);

  const productImageData = images.get(productId)?.get(productId)?.materialImages || [];
  const packageDetails = getPackageDetails(packagePath);

  useEffect(() => {
    const imageRequest: IReadImageRequest = {
      productCode: productId,
      packageCode: packageDetails.packageCode,
      packageType: packageDetails?.packageType,
    };
    if (!images.has(productId)) {
      dispatch(getImages(imageRequest));
    }
  }, []);

  const handleNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % productImageData.length);
  };

  const handlePrev = () => {
    setCurrentIndex(
      (prevIndex) => (prevIndex - 1 + productImageData.length) % productImageData.length
    );
  };

  const handleThumbnailClick = (index: number) => {
    setCurrentIndex(index);
  };

  return productImageData?.length ? (
    <Box sx={{ maxWidth: 600, margin: 'auto', height: '100%' }}>
      <Grid
        container
        spacing={2}
        sx={{ display: 'flex', justifyContent: 'center', height: '100%' }}
      >
        <Grid>
          <Box
            component="img"
            sx={{ width: '100%', height: '100%', justifyContent: 'center' }}
            src={productImageData[currentIndex].url}
            alt={`Image ${currentIndex + 1}`}
          />
        </Grid>
        <Grid xs={12} sx={{ display: 'block', alignSelf: 'flex-end' }}>
          <Grid>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2 }}>
              <IconButton onClick={handlePrev} data-testid="image-previous-button">
                <ChevronLeftIcon />
              </IconButton>

              {productImageData.map((img, index) => (
                <Box
                  data-testid="image-thumbnail"
                  key={index + 'product-image'}
                  component="img"
                  src={img.url}
                  alt={`Thumbnail ${index + 1}`}
                  onClick={() => handleThumbnailClick(index)}
                  sx={{
                    width: 150,
                    height: 120,
                    objectFit: 'cover',
                    borderRadius: 1,
                    cursor: 'pointer',
                    border: currentIndex === index ? '2px solid #1976d2' : '2px solid transparent',
                    transition: 'border 0.3s',
                  }}
                />
              ))}
              <IconButton onClick={handleNext} data-testid="image-next-button">
                <ChevronRightIcon />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  ) : (
    <Box>
      <img
        src={productImageNotFound}
        style={{ height: '90%', width: '90%', margin: '5px' }}
        alt="product-image-notfound"
      />
    </Box>
  );
};

export default ProductImageGallery;
